import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { IoGameControllerOutline } from 'react-icons/io5';
import {
  setWalletSidebarOpen,
  toggleConsoleModal,
  toggleFriendListPanel,
  toggleNotificationSidebar,
  toggleWalletSidebar,
} from '../../state/uiSlice';
import { loadNotifications, resetUnseenNotifications } from '../../redux/actions/notifications'; 
import {
  CalendarIcon,
  ChartBarIcon,
  HomeIcon,
  BellIcon,
  BuildingStorefrontIcon,
  UsersIcon,
  WalletIcon,
  MapIcon
} from '@heroicons/react/24/outline';
import ProfileSwitchButton from '../ProfileSwitchButton';
import ConnectionsPanel from '../Connections/ConnectionsPanel';
import ExploreIcon from './ExploreIcon';
import { getStoredAuthToken } from '../../utils/authSession';
import MatrixConsoleModal from '../Console/MatrixConsoleModal';
import CryptoWalletSidebar from '../../widgets/CryptoWallet';

const BellWithNotifications = ({ unseenCount, isActive = false, iconClassName = 'sidebar-icon' }) => (
  <div className="sidebar-relative">
    <BellIcon
      className={`${iconClassName} ${isActive ? 'sidebar-icon-current' : 'sidebar-icon-default'}`}
      aria-hidden="true"
    />
    {unseenCount > 0 && (
      <span className="sidebar-badge">
        {unseenCount}
      </span>
    )}
  </div>
);

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

function renderNavigationIcon(IconComponent, className, extraProps = {}) {
  return <IconComponent className={className} aria-hidden="true" {...extraProps} />;
}

function scrollPageToTop() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

  if (document.documentElement) {
    document.documentElement.scrollTop = 0;
  }

  if (document.body) {
    document.body.scrollTop = 0;
  }

  document
    .querySelectorAll(
      '.main-feed-page-scrollable, .main-feed-page-main-content, .profile-main-content, .profile-main-area, .shop-profile-main-area, .events-page-main-content, .map-page-main, .marketplace-page-content'
    )
    .forEach((element) => {
      element.scrollTop = 0;
    });
}

export default function Sidebar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, currentProfile, subscriptions } = useSelector((state) => state.user);
  const { isFriendListPanelOpen, isNotificationSidebarOpen, isWalletSidebarOpen, isConsoleModalOpen, mode } = useSelector((state) => state.ui);
  const { unseenCount } = useSelector((state) => state.notifications);
  const seller = useSelector((state) => state.seller.seller || state.seller.activeSeller);
  const hostname =
    typeof window !== 'undefined'
      ? String(window.location.hostname || '').toLowerCase()
      : '';
  const isPublicHomeHost = hostname === '' || hostname === 'buzzvibe.com' || hostname === 'www.buzzvibe.com';
  const hasAuthenticatedUser = Boolean(isAuthenticated || user || getStoredAuthToken());
  const homeHref = hasAuthenticatedUser ? '/home-page' : '/';
  const isFeedCurrent = location.pathname === '/home';
  const isHomeCurrent =
    (hasAuthenticatedUser && location.pathname === '/home-page') ||
    (!hasAuthenticatedUser && isPublicHomeHost && location.pathname === '/');

  const userId = user?._id;
  const sellerId = seller?._id;
  const profileId = currentProfile === 'User' ? userId : sellerId;

  useEffect(() => {
    if (profileId) {
      dispatch(loadNotifications(currentProfile, profileId));
    }
  }, [dispatch, currentProfile, profileId]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || '');
    const requestedOverlay = String(searchParams.get('overlay') || '').trim().toLowerCase();

    if (requestedOverlay !== 'wallet') {
      return;
    }

    dispatch(setWalletSidebarOpen({ isOpen: true, type: null }));

    if (!hasAuthenticatedUser) {
      return;
    }

    searchParams.delete('overlay');

    const nextSearch = searchParams.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash || ''}`, { replace: true });
  }, [dispatch, hasAuthenticatedUser, location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    scrollPageToTop();

    if (typeof window !== 'undefined') {
      const frameId = window.requestAnimationFrame(scrollPageToTop);
      return () => window.cancelAnimationFrame(frameId);
    }

    return undefined;
  }, [location.pathname]);

  const handleNavigationClick = (item) => {
    if (item.onClick === 'openConnections') {
      dispatch(toggleFriendListPanel());
      return;
    }
    if (item.onClick === 'openNotifications') {
      dispatch(toggleNotificationSidebar());
      dispatch(resetUnseenNotifications(profileId, currentProfile));
      return;
    }
    if (item.onClick === 'openWalletSidebar') {
      dispatch(toggleWalletSidebar());
      return;
    }
    if (item.onClick === 'openConsole') {
      dispatch(toggleConsoleModal());
      return;
    }

    scrollPageToTop();

    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(scrollPageToTop);
    }
  };

  const getDesktopIconNode = (item) => {
    if (item.id === 'notifications') {
      return (
        <BellWithNotifications
          unseenCount={unseenCount}
          isActive={item.current}
          iconClassName="sidebar-icon-large"
        />
      );
    }

    return renderNavigationIcon(
      item.icon,
      classNames(
        item.current ? 'sidebar-icon-current' : 'sidebar-icon-default',
        'sidebar-icon-large'
      )
    );
  };

  const getMobileIconNode = (item) => {
    if (item.id === 'notifications') {
      return <BellWithNotifications unseenCount={unseenCount} isActive={item.current} />;
    }

    return renderNavigationIcon(
      item.icon,
      classNames(
        item.current ? 'sidebar-icon-current' : 'sidebar-icon-default',
        'sidebar-icon'
      )
    );
  };

  // Define your navigation array with a unique id for each item
  let navigation = [
    { id: 'feed', name: 'Feed', icon: ExploreIcon, href: '/home', current: isFeedCurrent },
    { id: 'home', name: 'Home', icon: HomeIcon, href: homeHref, current: isHomeCurrent },
    { id: 'marketplace', name: 'Marketplace', icon: BuildingStorefrontIcon, href: '/marketplace', current: location.pathname === '/marketplace' },
    { id: 'events', name: 'Events', icon: CalendarIcon, href: '/events', current: location.pathname === '/events' },
    { id: 'notifications', name: 'Notifications', icon: BellIcon, href: '#', current: isNotificationSidebarOpen, onClick: 'openNotifications', hideOnDesktop: true, hideOnMobile: true },
    { id: 'map', name: 'Map', icon: MapIcon, href: '/map', current: location.pathname === '/map' },
    { id: 'connections', name: 'Connections', icon: UsersIcon, href: '#', current: isFriendListPanelOpen, onClick: 'openConnections' },
    { id: 'wallet', name: 'Wallet', icon: WalletIcon, href: '#', current: isWalletSidebarOpen, onClick: 'openWalletSidebar' },
  ];

  navigation.push({
    id: 'console',
    name: 'Console',
    icon: IoGameControllerOutline,
    href: '#',
    current: isConsoleModalOpen,
    onClick: 'openConsole',
  });

  if (currentProfile !== 'User') {
    navigation.push({
      id: 'dashboard',
      name: 'Dashboard',
      icon: ChartBarIcon,
      href: '/dashboard',
      current: location.pathname === '/dashboard' || location.pathname.startsWith('/dashboard/'),
    });
  }

  return (
    <>
      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav lg:hidden">
        <nav className="mobile-nav-icons">
          {navigation
            .filter((item) => item.id !== 'notifications' && !item.hideOnMobile)
            .map((item) => {
              const isAction = item.href === '#';
              const isExternal = /^https?:\/\//.test(item.href);
              return isAction ? (
                <button
                  key={item.id}
                  type="button"
                  className={classNames(
                    item.current ? 'mobile-nav-item-active' : 'mobile-nav-item',
                    'mobile-nav-link'
                  )}
                  onClick={() => handleNavigationClick(item)}
                  aria-label={item.name}
                  aria-pressed={item.current}
                >
                  {getMobileIconNode(item)}
                </button>
              ) : isExternal ? (
                <a
                  key={item.id}
                  href={item.href}
                  className={classNames(
                    item.current ? 'mobile-nav-item-active' : 'mobile-nav-item',
                    'mobile-nav-link'
                  )}
                  onClick={() => handleNavigationClick(item)}
                >
                  {getMobileIconNode(item)}
                </a>
              ) : (
                <Link
                  key={item.id}
                  to={item.href}
                  className={classNames(
                    item.current ? 'mobile-nav-item-active' : 'mobile-nav-item',
                    'mobile-nav-link'
                  )}
                  onClick={() => handleNavigationClick(item)}
                >
                  {getMobileIconNode(item)}
                </Link>
              );
            })}
        </nav>
      </div>

      {/* Sidebar for Larger Screens */}
      <div id="desktop-sidebar" className="sidebar-desktop hidden lg:flex">
        <div className="sidebar-container">
          <div className="sidebar-content">
            <ProfileSwitchButton className="sidebar-profile-toolbar-avatar" />
            <nav className="sidebar-nav-large" aria-label="Sidebar">
              {navigation.filter((item) => !item.hideOnDesktop).map((item) => {
                const isExternal = /^https?:\/\//.test(item.href);
                const itemClassName = classNames(
                  item.current
                    ? 'sidebar-nav-item-current'
                    : 'sidebar-nav-item-default',
                  'sidebar-nav-item-large'
                );

                const iconNode = getDesktopIconNode(item);

                if (item.href === '#') {
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={itemClassName}
                      onClick={() => handleNavigationClick(item)}
                      aria-label={item.name}
                      aria-pressed={item.current}
                    >
                      {iconNode}
                    </button>
                  );
                }

                if (isExternal) {
                  return (
                    <a
                      key={item.id}
                      href={item.href}
                      className={itemClassName}
                      onClick={() => handleNavigationClick(item)}
                    >
                      {iconNode}
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    className={itemClassName}
                    onClick={() => handleNavigationClick(item)}
                  >
                    {iconNode}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      </div>

      {/* Conditional Panels */}
      {isFriendListPanelOpen && <ConnectionsPanel />}
      {isWalletSidebarOpen && <CryptoWalletSidebar />}
      <MatrixConsoleModal
        isOpen={isConsoleModalOpen}
        onClose={() => dispatch(toggleConsoleModal())}
        user={user}
        seller={seller}
        currentProfile={currentProfile}
        subscriptions={subscriptions}
        mode={mode}
      />
    </>
  );
}



